import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const FeedbackPopup = lazy(() => import('./components/FeedbackPopup').then(m => ({
  default: m.FeedbackPopup
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
// Lightweight loading placeholder
function SectionLoader() {
  return <div className="w-full py-24 flex items-center justify-center" data-id="element-0">
      <div className="w-8 h-8 border-3 border-haa-blue/20 border-t-haa-blue rounded-full animate-spin" data-id="element-1" />
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    language,
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      const timer = setTimeout(() => {
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
        }
        setPendingScroll(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-2" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-3">
      <Navbar data-id="element-4" />
      <main data-id="element-5">
        <Hero data-id="element-6" />
        <ConsularBanner data-id="element-7" />
        <Mission data-id="element-8" />
        <Suspense fallback={<SectionLoader data-id="element-10" />} data-id="element-9">
          <MakeDifferenceCTA data-id="element-11" />
          <div className="section-divider-blue-accent" data-id="element-12" />
          <NewsBlog data-id="element-13" />
          <div className="section-divider-blue-accent" data-id="element-14" />
          <MediaScroller data-id="element-15" />
          <div className="section-divider-blue-accent" data-id="element-16" />
          <CommunityStrength data-id="element-17" />
          <div className="section-divider-blue-accent" data-id="element-18" />
          <MissionPanels data-id="element-19" />
          <div className="section-divider-blue-accent" data-id="element-20" />
          <Leadership data-id="element-21" />
          <div className="section-divider-blue-accent" data-id="element-22" />
          <AdvisoryCouncil data-id="element-23" />
          <MakeDifferenceCTA data-id="element-24" />
          <div className="section-divider-blue-accent" data-id="element-25" />
          <BusinessInvestments data-id="element-26" />
          <div className="section-divider-blue-accent" data-id="element-27" />
          <ConsularAffairs data-id="element-28" />
          <div className="section-divider-blue-accent" data-id="element-29" />
          <SocialMediaVoices data-id="element-30" />
          <div className="section-divider-blue-accent" data-id="element-31" />
          <NonprofitDirectory data-id="element-32" />
          <div className="section-divider-blue-accent" data-id="element-33" />
          <HondurasQuiz data-id="element-34" />
          <div className="section-divider-blue-accent" data-id="element-35" />
          <ExploreHonduras data-id="element-36" />
          <div className="section-divider-blue-accent" data-id="element-37" />
          <FAQ data-id="element-38" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-39">
        <Footer data-id="element-40" />
        <FeedbackPopup data-id="element-41" />
        <ChatbaseWidget data-id="element-42" />
      </Suspense>
    </div>;
}
export function App() {
  const [showLanding, setShowLanding] = useState(true);
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash === '#/business-map');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      setShowBusinessMap(hash === '#/business-map');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    setShowLanding(true);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-44" />} data-id="element-43">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-45" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-46">
        <Suspense fallback={<SectionLoader data-id="element-48" />} data-id="element-47">
          <ConsularPage data-id="element-49" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-50">
        <Suspense fallback={<SectionLoader data-id="element-52" />} data-id="element-51">
          <BusinessMapPage data-id="element-53" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-54">
        <Suspense fallback={<SectionLoader data-id="element-56" />} data-id="element-55">
          <MemberProfilePage slug={memberSlug} data-id="element-57" />
        </Suspense>
      </LanguageProvider>;
  }
  return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-58">
      <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-59" />
    </LanguageProvider>;
}